import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AiFeedbackButtons from './AiFeedbackButtons';

const AiMessageBubble = ({ role, content, suggestedAction, options, onActionClick, onOptionClick, requestId, via, feedback, onFeedback }) => {
  const isUser = role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
        px: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          px: 2,
          py: 1.25,
          borderRadius: 2,
          bgcolor: isUser ? 'primary.main' : 'action.hover',
          color: isUser ? 'common.white' : 'text.primary',
          boxShadow: 1,
          ...(isUser
            ? { borderBottomRightRadius: 0 }
            : { borderBottomLeftRadius: 0 }),
          '& table': {
            borderCollapse: 'collapse',
            width: '100%',
            my: 1,
            fontSize: '0.85rem',
          },
          '& th, & td': {
            border: '1px solid',
            borderColor: 'divider',
            px: 1,
            py: 0.5,
            textAlign: 'left',
          },
          '& th': {
            bgcolor: isUser ? 'primary.dark' : 'action.selected',
            fontWeight: 'bold',
          },
          '& h1, & h2, & h3, & h4': { mt: 1.5, mb: 0.75, fontSize: '1rem' },
          '& p': { my: 0.75, fontSize: '0.875rem' },
          '& ul, & ol': { pl: 3, my: 0.75 },
          '& hr': { my: 1.5, borderColor: isUser ? 'primary.light' : 'divider' },
          '& code': {
            bgcolor: isUser ? 'primary.dark' : 'action.selected',
            px: 0.5,
            borderRadius: 0.5,
            fontSize: '0.8rem',
          },
          '& pre': {
            bgcolor: isUser ? 'primary.dark' : 'action.selected',
            p: 1.5,
            borderRadius: 1,
            overflowX: 'auto',
            my: 1,
          },
        }}
      >
        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {content}
          </Typography>
        ) : (
          <>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            {via && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.6 }}>
                via {via.charAt(0).toUpperCase() + via.slice(1)}
              </Typography>
            )}
            <AiFeedbackButtons
              requestId={requestId}
              value={feedback}
              onRate={(rating, comment) => onFeedback?.(rating, comment)}
            />
            {suggestedAction && (
              <Card
                variant="outlined"
                sx={{
                  mt: 1,
                  bgcolor: 'success.main',
                  borderColor: 'success.dark',
                }}
              >
                <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'success.contrastText', fontWeight: 700, textTransform: 'uppercase' }}
                  >
                    Quick action
                  </Typography>
                  {suggestedAction.description && (
                    <Typography variant="body2" sx={{ my: 0.5, color: 'success.contrastText', opacity: 0.85 }}>
                      {suggestedAction.description}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    onClick={() => onActionClick?.(suggestedAction.url)}
                    sx={{ mt: 0.5, bgcolor: 'success.dark', '&:hover': { bgcolor: 'success.dark', filter: 'brightness(1.15)' } }}
                  >
                    {suggestedAction.label}
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </Box>

      {/* Backend-driven option chips — rendered below the bubble, outside it */}
      {!isUser && options && options.length > 0 && (
        <Box sx={{ maxWidth: '92%', mt: 0.75, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {options.map((group, gi) => (
            <Box key={gi}>
              {group.label && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.4, fontWeight: 600 }}
                >
                  {group.label}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                {group.items.map((item) => (
                  <Chip
                    key={item.value}
                    label={item.label}
                    size="small"
                    variant="outlined"
                    onClick={() => onOptionClick?.(item.value, via)}
                    sx={{
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'primary.main',
                        color: 'common.white',
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AiMessageBubble;
